<template>
  <div ref="pdfContainer" class="pdf-container">
    <canvas ref="pdfCanvas"></canvas>
    <div ref="annotationLayer" class="annotationLayer"></div>
  </div>
</template>

<script>
import * as pdfjsLib from 'pdfjs-dist'
import pdfjsWorker from 'pdfjs-dist/build/pdf.worker.entry'

// ✅ Manual import paths for v2.16.105
import { PDFLinkService } from 'pdfjs-dist/lib/web/pdf_link_service.js'
import { render as renderAnnotationLayer } from 'pdfjs-dist/lib/web/annotation_layer_builder.js'

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker

export default {
  name: 'PdfViewer',
  props: {
    src: {
      type: String,
      required: true
    },
    /** Optional max CSS width in pixels (useful on desktop); phones will naturally use 100% */
    maxWidth: {
      type: [Number, String],
      default: '100%'
    },
    /** Page number to render (simple single-page viewer) */
    pageNumber: {
      type: Number,
      default: 1
    },
    /** Minimum and maximum scale clamps to avoid extreme zoom on odd layouts */
    minScale: {
      type: Number,
      default: 0.5
    },
    maxScale: {
      type: Number,
      default: 3
    }
  },
  data() {
    return {
      pdf: null,
      page: null,
      currentScale: 1,
      linkService: null,
      resizeObserver: null,
      isRendering: false
    }
  },
  async mounted() {
    await this.loadPdf()
    this.setupAutosize()
  },
  beforeUnmount() {
    this.teardownAutosize()
  },
  methods: {
    async loadPdf() {
      try {
        const loadingTask = pdfjsLib.getDocument(this.src)
        this.pdf = await loadingTask.promise
        this.page = await this.pdf.getPage(this.pageNumber)

        // Link service for clickable links (mailto:, http(s), etc.)
        this.linkService = new PDFLinkService()
        this.linkService.setDocument(this.pdf)
        this.linkService.setViewer({
          scrollPageIntoView: ({ pageNumber }) => {
            // Could hook into a multi-page scroller; for now log for debug.
            console.log('Navigate to page', pageNumber)
          }
        })
        this.linkService.page = this.pageNumber

        // Initial render sized to container
        this.renderToContainerWidth()
      } catch (error) {
        console.error('Failed to render PDF:', error)
      }
    },

    setupAutosize() {
      // Re-render on container size changes (covers phone rotation + dynamic UI)
      const container = this.$refs.pdfContainer
      if ('ResizeObserver' in window && container) {
        this.resizeObserver = new ResizeObserver(() => {
          this.renderToContainerWidth()
        })
        this.resizeObserver.observe(container)
      } else {
        // Fallback: window resize/orientation events
        window.addEventListener('resize', this.renderToContainerWidth, { passive: true })
        window.addEventListener('orientationchange', this.renderToContainerWidth, { passive: true })
      }
    },

    teardownAutosize() {
      if (this.resizeObserver) {
        this.resizeObserver.disconnect()
        this.resizeObserver = null
      } else {
        window.removeEventListener('resize', this.renderToContainerWidth)
        window.removeEventListener('orientationchange', this.renderToContainerWidth)
      }
    },

    // Compute a scale that fits the page to the container width (great for phones)
    computeFittedScale() {
      if (!this.page) return 1
      const container = this.$refs.pdfContainer
      const unscaledViewport = this.page.getViewport({ scale: 1 })
      const containerCSSWidth = container.clientWidth || unscaledViewport.width
      let scale = containerCSSWidth / unscaledViewport.width

      // Clamp scale to avoid silly extremes on very small/very large screens
      scale = Math.min(Math.max(scale, this.minScale), this.maxScale)
      return scale
    },

    async renderToContainerWidth() {
      if (!this.page || this.isRendering) return
      this.isRendering = true

      try {
        const canvas = this.$refs.pdfCanvas
        const ctx = canvas.getContext('2d')
        const annotationsDiv = this.$refs.annotationLayer

        const scale = this.computeFittedScale()
        this.currentScale = scale
        const viewport = this.page.getViewport({ scale })

        // High-DPI rendering: back buffer sized to DPR, CSS size stays in CSS pixels
        const dpr = window.devicePixelRatio || 1
        const outputWidth = Math.floor(viewport.width * dpr)
        const outputHeight = Math.floor(viewport.height * dpr)

        // Set canvas pixel buffer
        canvas.width = outputWidth
        canvas.height = outputHeight

        // Set canvas CSS size
        canvas.style.width = `${viewport.width}px`
        canvas.style.height = `${viewport.height}px`

        // Render page
        await this.page.render({
          canvasContext: ctx,
          viewport,
          transform: dpr !== 1 ? [dpr, 0, 0, dpr, 0, 0] : undefined
        }).promise

        // Prepare annotation layer (CSS pixels, not DPR pixels)
        annotationsDiv.innerHTML = ''
        annotationsDiv.style.width = `${viewport.width}px`
        annotationsDiv.style.height = `${viewport.height}px`

        await renderAnnotationLayer({
          viewport,
          div: annotationsDiv,
          annotations: await this.page.getAnnotations(),
          page: this.page,
          linkService: this.linkService,
          renderInteractiveForms: true,
          annotationCanvasMap: new Map()
        })

        // Ensure links are usable on touch devices
        // (pdf.js sets tabindex, but we ensure pointer-events)
        annotationsDiv.querySelectorAll('a[href]').forEach(a => {
          a.setAttribute('rel', 'noopener noreferrer')
          a.setAttribute('target', '_blank') // external links open outside
        })
      } catch (e) {
        console.error('Render failed:', e)
      } finally {
        this.isRendering = false
      }
    }
  },
  watch: {
    // Re-render if the source or page changes
    src: {
      immediate: false,
      async handler() {
        if (this.pdf) {
          this.pdf = null
          this.page = null
        }
        await this.loadPdf()
      }
    },
    pageNumber: {
      immediate: false,
      async handler() {
        if (this.pdf) {
          this.page = await this.pdf.getPage(this.pageNumber)
          await this.renderToContainerWidth()
        }
      }
    }
  }
}
</script>

<style scoped>
.pdf-container {
  position: relative;
  width: 100%;
  max-width: v-bind(maxWidth);
  margin: 0 auto;
  /* On phones, allow natural page height and scrolling */
  overflow: hidden; /* container holds one page; parent can scroll */
  touch-action: manipulation; /* improves tap responsiveness on mobile */
  -webkit-tap-highlight-color: transparent;
}

canvas {
  display: block;
  width: 100%;   /* keeps canvas responsive */
  height: auto;  /* maintain aspect ratio */
}

.annotationLayer {
  position: absolute;
  top: 0;
  left: 0;
  /* Must be interactable on touch devices for links/buttons in PDFs */
  pointer-events: auto;
  z-index: 10;
  /* Let taps land accurately */
  touch-action: manipulation;
}

/* Improve link tap targets within annotation layer on small screens */
.annotationLayer a {
  display: inline-block;
  min-height: 32px; /* friendlier touch area */
}
</style>
